import { useEffect, useRef, useState } from "react";
import { Upload, Images, Trash2, X, Crosshair } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, fileUrl, coverSrc, coverPosition } from "@/lib/api";
import { Thumb } from "@/components/Bits";

const ACCEPT = "image/jpeg,image/png,image/webp";
const btn = "inline-flex items-center gap-2 rounded-sm border border-line bg-white px-4 py-2.5 text-sm text-ink transition-colors hover:border-fog";

/**
 * Cover image management for projects and spaces: upload, pick from project files,
 * set the focal point and remove. Uploads land in the project's own file storage;
 * for an unsaved record they are held unattached and linked once it is created.
 */
export default function CoverImageField({ projectId, value, onChange, label = "Cover Image" }) {
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(false);
  const [focusing, setFocusing] = useState(false);
  const [images, setImages] = useState([]);
  const fileRef = useRef();

  useEffect(() => {
    if (!picking || !projectId) return;
    api
      .get("/files", { params: { project_id: projectId } })
      .then((r) => setImages(r.data.filter((f) => (f.content_type || "").startsWith("image/"))))
      .catch(() => setImages([]));
  }, [picking, projectId]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ACCEPT.split(",").includes(file.type)) {
      toast.error("Please choose a JPG, PNG or WebP image.");
      return;
    }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", "Renderings");
    if (projectId) fd.append("project_id", projectId);
    fd.append("visible_to_client", "true");
    try {
      const { data } = await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange({ cover_file_id: data.id, cover_image: "", cover_focus: "" });
      toast.success("Cover image uploaded");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const choose = (f) => {
    onChange({ cover_file_id: f.id, cover_image: "", cover_focus: "" });
    setPicking(false);
  };

  const clear = () => {
    setFocusing(false);
    onChange({ cover_file_id: "", cover_image: "", cover_focus: "" });
  };

  const pickFocus = (e) => {
    if (!focusing) return;
    const box = e.currentTarget.getBoundingClientRect();
    const x = Math.round(((e.clientX - box.left) / box.width) * 100);
    const y = Math.round(((e.clientY - box.top) / box.height) * 100);
    onChange({ cover_focus: `${Math.min(100, Math.max(0, x))}% ${Math.min(100, Math.max(0, y))}%` });
  };

  const src = coverSrc(value);

  return (
    <div data-testid="cover-image-field">
      <span className="overline mb-2 block">{label}</span>
      <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
        <div
          onClick={pickFocus}
          data-testid="cover-focus-target"
          className={`relative aspect-[16/10] w-full overflow-hidden rounded-sm border bg-[#F1F0EC] sm:w-64 sm:shrink-0 ${
            focusing ? "cursor-crosshair border-lime" : "border-line"
          }`}
        >
          {src ? (
            <>
              <img
                data-testid="cover-image-preview"
                src={src}
                alt="Cover preview"
                style={{ objectPosition: coverPosition(value) }}
                className="h-full w-full object-cover"
              />
              {focusing && (
                <span
                  data-testid="cover-focus-marker"
                  style={{ left: coverPosition(value).split(" ")[0], top: coverPosition(value).split(" ")[1] }}
                  className="pointer-events-none absolute h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-lime bg-ink/30"
                />
              )}
            </>
          ) : (
            <Thumb className="h-full w-full" rounded="rounded-none" />
          )}
        </div>
        <div className="flex flex-wrap gap-3">
          <label className={`${btn} cursor-pointer`} data-testid="cover-upload-label">
            <Upload className="h-4 w-4" strokeWidth={1.5} />
            {busy ? "Uploading…" : src ? "Replace Image" : "Upload Image"}
            <input
              ref={fileRef}
              data-testid="cover-upload-input"
              type="file"
              accept={ACCEPT}
              className="hidden"
              onChange={upload}
              disabled={busy}
            />
          </label>
          {projectId && (
            <button type="button" data-testid="cover-choose-btn" onClick={() => setPicking(true)} className={btn}>
              <Images className="h-4 w-4" strokeWidth={1.5} />
              Choose from Files
            </button>
          )}
          {src && (
            <button
              type="button"
              data-testid="cover-focus-btn"
              onClick={() => setFocusing((v) => !v)}
              className={`${btn} ${focusing ? "border-lime bg-lime" : ""}`}
            >
              <Crosshair className="h-4 w-4" strokeWidth={1.5} />
              {focusing ? "Done adjusting" : "Adjust focal point"}
            </button>
          )}
          {src && (
            <button
              type="button"
              data-testid="cover-remove-btn"
              onClick={clear}
              className="inline-flex items-center gap-2 rounded-sm border border-line bg-white px-4 py-2.5 text-sm text-stone transition-colors hover:border-clay hover:text-clay"
            >
              <Trash2 className="h-4 w-4" strokeWidth={1.5} />
              Remove
            </button>
          )}
          <p className="w-full text-xs leading-relaxed text-fog">
            {focusing
              ? "Click the part of the image that must always stay in frame."
              : "JPG, PNG or WebP. Saved to this project’s files and shown to the client."}
          </p>
        </div>
      </div>

      {picking && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 p-0 sm:items-center sm:p-6" data-testid="cover-picker">
          <div className="max-h-[85vh] w-full max-w-3xl overflow-y-auto rounded-t-sm bg-white p-6 sm:rounded-sm sm:p-8">
            <div className="mb-6 flex items-center justify-between">
              <h3 className="font-serif text-xl text-ink">Choose a cover image</h3>
              <button
                type="button"
                data-testid="cover-picker-close"
                onClick={() => setPicking(false)}
                className="rounded-sm border border-line p-1.5 text-stone transition-colors hover:border-fog"
                aria-label="Close"
              >
                <X className="h-4 w-4" strokeWidth={1.5} />
              </button>
            </div>
            {images.length === 0 ? (
              <p className="py-10 text-center text-sm text-fog">
                No images on this project yet. Upload one above or in the Files tab.
              </p>
            ) : (
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                {images.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    data-testid={`cover-pick-${f.id}`}
                    onClick={() => choose(f)}
                    className={`group overflow-hidden rounded-sm border text-left transition-colors ${
                      value?.cover_file_id === f.id ? "border-lime" : "border-line hover:border-fog"
                    }`}
                  >
                    <img src={fileUrl(f.id)} alt={f.original_filename} className="aspect-[4/3] w-full object-cover bg-line" />
                    <span className="block truncate px-3 py-2 text-xs text-stone">{f.original_filename}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
